import { isSubmitting, submit } from 'redux-form';
import { useFormContext } from 'react-hook-form';
import { useCallback } from 'react';
import { WithServiceId } from '@mews-commander/types';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { getResourceCategoryName } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { EditDialogForm } from '@mews-commander/legacy-form';
import { EditDialog } from '../../../enums';
import { AvailabilityAdjustmentFormValues, WithFormArrayFields } from '../../../types';
import { FIELD_ARRAY_AVAILABILITY, RESOURCE_CATEGORY_ID } from '../../../constants';
import BulkAdjustAvailabilitiesContent from './BulkAdjustAvailabilitiesContent';
import { useBulkUpdate } from './useBulkUpdate';
import { BulkAdjustAvailabilitiesFormValues } from './types';

interface BulkAdjustAvailabilitiesDialogProps extends WithServiceId, WithFormArrayFields {
    onClose: () => void;
    resourceCategoryId: string;
    active: boolean;
    availabilityBlockId: string;
    rowIndex: number;
}

export const BulkAdjustAvailabilitiesDialog = ({
    onClose,
    active,
    availabilityBlockId,
    resourceCategoryId,
    fields,
    serviceId,
    rowIndex,
}: BulkAdjustAvailabilitiesDialogProps) => {
    const { watch } = useFormContext<AvailabilityAdjustmentFormValues>();
    const watchedResourceCategoryId = watch(`${FIELD_ARRAY_AVAILABILITY}.${rowIndex}.${RESOURCE_CATEGORY_ID}`);

    const resourceCategoryField = fields[rowIndex];
    const dispatch = useCommanderThunkDispatch();

    const updateAvailabilities = useBulkUpdate(
        serviceId,
        rowIndex,
        availabilityBlockId,
        watchedResourceCategoryId,
        resourceCategoryField,
        onClose
    );

    const resourceCategoryName =
        useCommanderSelector((state) => getResourceCategoryName(state, { id: resourceCategoryId, serviceId })) ?? '';

    const submitting = useCommanderSelector(isSubmitting(EditDialog.BulkAdjustAvailabilities));

    const onSubmit = useCallback(
        (values: BulkAdjustAvailabilitiesFormValues) => {
            updateAvailabilities(values);
        },
        [updateAvailabilities]
    );

    return (
        <EditDialogForm
            handleSubmit={() => dispatch(submit(EditDialog.BulkAdjustAvailabilities))}
            loading={submitting}
            closeDialog={onClose}
            active={active}
            primaryButtonTextKey={TextKey.Add}
            title={
                <LocalizedText
                    textKey={TextKey.BulkAdjustAvailabilitiesDialogTitle}
                    substitutes={{ resourceCategoryName }}
                />
            }
        >
            <BulkAdjustAvailabilitiesContent
                id={availabilityBlockId}
                resourceCategoryId={resourceCategoryId}
                availabilityBlockId={availabilityBlockId}
                serviceId={serviceId}
                form={EditDialog.BulkAdjustAvailabilities}
                onSubmit={onSubmit}
                field={fields[rowIndex]}
            />
        </EditDialogForm>
    );
};
